import { Link } from "react-router-dom";
import { ArrowRight, Calculator, FileText, Landmark, Building, Globe, ScrollText, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

const HERO_IMAGE = "https://images.pexels.com/photos/7642116/pexels-photo-7642116.jpeg";

const services = [
  {
    id: "isee",
    title: "ISEE e DSU",
    description: "Calcolo dell'Indicatore della Situazione Economica per accedere a bonus e agevolazioni.",
    icon: Calculator,
    featured: true,
  },
  {
    id: "730",
    title: "Modello 730",
    description: "Dichiarazione dei redditi con rimborso diretto in busta paga o pensione.",
    icon: FileText,
    featured: true,
  },
  {
    id: "pensioni",
    title: "Pensioni",
    description: "Pratiche pensionistiche: vecchiaia, anticipata, invalidità, reversibilità.",
    icon: Landmark,
  },
  {
    id: "inps",
    title: "Pratiche INPS",
    description: "NASPI, bonus maternità, assegno unico, congedi parentali.",
    icon: Building,
  },
  {
    id: "immigrazione",
    title: "Immigrazione",
    description: "Permessi di soggiorno, ricongiungimenti, cittadinanza.",
    icon: Globe,
  },
  {
    id: "successioni",
    title: "Successioni",
    description: "Dichiarazioni di successione e pratiche ereditarie.",
    icon: ScrollText,
  },
];

const benefits = [
  "Assistenza personalizzata",
  "Professionisti qualificati",
  "Tariffe trasparenti",
  "Appuntamenti flessibili",
];

const Home = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-[85vh] flex items-center">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_IMAGE})` }}
        />
        <div className="absolute inset-0 hero-overlay" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-2xl">
            <span className="inline-block px-4 py-2 bg-red-600 text-white text-sm font-semibold rounded-full mb-6 animate-fade-in-up">
              Margherita di Savoia
            </span>
            <h1 className="font-heading text-5xl md:text-7xl font-black tracking-tighter text-white mb-6 animate-fade-in-up stagger-1">
              CAF & Patronato EPAS
            </h1>
            <p className="text-xl md:text-2xl text-slate-200 mb-8 leading-relaxed animate-fade-in-up stagger-2">
              ISEE, 730, pensioni, pratiche INPS e immigrazione. 
              <span className="text-white font-semibold"> Ti seguiamo passo passo</span>, senza giri inutili.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up stagger-3">
              <Link to="/prenota">
                <Button 
                  size="lg" 
                  className="bg-red-600 hover:bg-red-700 text-white rounded-full px-8 py-6 text-lg font-semibold shadow-lg shadow-red-600/30"
                  data-testid="hero-cta-prenota"
                >
                  Prenota Appuntamento
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/servizi">
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="bg-white/10 border-white/30 text-white hover:bg-white/20 rounded-full px-8 py-6 text-lg font-semibold backdrop-blur-sm"
                  data-testid="hero-cta-servizi"
                >
                  Scopri i Servizi
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Partner Ribbon */}
      <section className="partner-ribbon py-8 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-center gap-8 md:gap-16">
            <span className="text-sm font-semibold tracking-wider uppercase text-slate-500">Affiliato a:</span>
            <span className="text-lg font-bold text-slate-700">CAF Italia Srl</span>
            <span className="text-lg font-bold text-slate-700">EPAS</span>
            <span className="text-lg font-bold text-slate-700">FNA Confsal</span>
            <span className="text-lg font-bold text-blue-800">U.Di.Con</span>
          </div>
        </div>
      </section>

      {/* Services Bento Grid */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-16">
            <span className="text-sm font-semibold tracking-[0.1em] uppercase text-slate-500 mb-4 block">
              I Nostri Servizi
            </span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-slate-900 mb-4">
              Come possiamo aiutarti
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl">
              Offriamo assistenza completa per tutte le tue pratiche fiscali, previdenziali e burocratiche.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            {/* Featured Services - Large Cards */}
            {services.filter(s => s.featured).map((service, idx) => (
              <Link
                key={service.id}
                to={`/documenti-${service.id}`}
                className={`md:col-span-6 group card-hover bg-white border border-slate-200 rounded-2xl p-8 shadow-sm`}
                data-testid={`service-card-${service.id}`}
              >
                <div className={`w-14 h-14 rounded-xl flex items-center justify-center mb-6 ${
                  idx === 0 ? 'bg-blue-900' : 'bg-slate-900'
                }`}>
                  <service.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-heading text-2xl md:text-3xl font-bold text-slate-900 mb-3 group-hover:text-blue-900 transition-colors">
                  {service.title}
                </h3>
                <p className="text-lg text-slate-600 mb-4">
                  {service.description}
                </p>
                <span className="inline-flex items-center text-blue-900 font-semibold group-hover:gap-2 transition-all">
                  Scopri di più
                  <ArrowRight className="w-5 h-5 ml-1 group-hover:translate-x-1 transition-transform" />
                </span>
              </Link>
            ))}

            {/* Regular Services - Smaller Cards */}
            {services.filter(s => !s.featured).map((service) => (
              <Link
                key={service.id}
                to="/servizi"
                className="md:col-span-3 group card-hover bg-slate-50 border border-slate-200 rounded-2xl p-6"
                data-testid={`service-card-${service.id}`}
              >
                <service.icon className="w-8 h-8 text-blue-900 mb-4" />
                <h3 className="font-heading text-lg font-bold text-slate-900 mb-2 group-hover:text-blue-900 transition-colors">
                  {service.title}
                </h3>
                <p className="text-sm text-slate-600">
                  {service.description}
                </p>
              </Link>
            ))}
          </div>

          <div className="mt-12 text-center">
            <Link to="/servizi">
              <Button 
                variant="outline" 
                size="lg"
                className="rounded-full px-8"
                data-testid="view-all-services"
              >
                Vedi Tutti i Servizi
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 md:py-32 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-sm font-semibold tracking-[0.1em] uppercase text-slate-500 mb-4 block">
                Perché Sceglierci
              </span>
              <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-slate-900 mb-6">
                Al tuo fianco per ogni pratica
              </h2>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                Da anni assistiamo i cittadini di Margherita di Savoia e zone limitrofe con professionalità e dedizione. Il nostro obiettivo è semplificare la burocrazia per te.
              </p>
              <ul className="space-y-4">
                {benefits.map((benefit, idx) => (
                  <li key={idx} className="flex items-center gap-3">
                    <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
                    <span className="text-lg text-slate-700">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative">
              <img
                src="https://images.pexels.com/photos/1653898/pexels-photo-1653898.jpeg"
                alt="Coppia felice"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-red-600 text-white p-6 rounded-xl shadow-lg">
                <p className="text-3xl font-bold font-heading">15+</p>
                <p className="text-sm">Anni di esperienza</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-blue-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-white mb-6">
            Hai bisogno di assistenza?
          </h2>
          <p className="text-xl text-blue-100 mb-10 max-w-2xl mx-auto">
            Prenota un appuntamento oggi stesso. Ti aiuteremo a risolvere ogni pratica in modo rapido e professionale.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/prenota">
              <Button 
                size="lg" 
                className="bg-red-600 hover:bg-red-700 text-white rounded-full px-8 py-6 text-lg font-semibold"
                data-testid="cta-prenota"
              >
                Prenota Appuntamento
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/contatti">
              <Button 
                size="lg" 
                variant="outline" 
                className="bg-transparent border-white text-white hover:bg-white/10 rounded-full px-8 py-6 text-lg font-semibold"
                data-testid="cta-contatti"
              >
                Contattaci
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
